// components/profile/sections/PortfolioSection.jsx
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { FormField } from '../components/FormField';
import { ImageUpload } from '../components/ImageUpload';
import { VideoUpload } from '../components/VideoUpload';
import { useProfileFormContext } from '../context/ProfileFormContext';
import { 
  TrashIcon,
  BriefcaseIcon,
  TrophyIcon,
  TagIcon,
  PlusIcon,
  SparklesIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline';
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';

const DeleteModal = ({ isOpen, onClose, onConfirm }) => {
  if (!isOpen) return null;
  
  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 'var(--z-modal, 9999)' }}>
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white shadow-2xl max-w-md w-full p-6 animate-fadeIn" style={{
        borderRadius: 'var(--radius-xl)',
        backgroundColor: 'var(--Surface-Primary)'
      }}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 flex items-center justify-center" style={{
            borderRadius: 'var(--radius-s)',
            backgroundColor: 'var(--Red-100)'
          }}>
            <ExclamationTriangleIcon className="w-5 h-5" style={{ color: 'var(--Red-600)' }} />
          </div>
          <h3 style={{
            fontSize: 'var(--font-size-subtitle1)',
            fontWeight: 'var(--font-weight-subtitle2)',
            color: 'var(--Text-Primary)'
          }}>Confirm Deletion</h3>
        </div>
        
        <p style={{ color: 'var(--Text-Secondary)', marginBottom: 'var(--spacing-l)' }}>
          Are you sure you want to remove this achievement? This action cannot be undone.
        </p>
        
        <div className="flex gap-3 justify-end">
          <button 
            onClick={onClose}
            className="px-6 py-2"
            style={{
              backgroundColor: 'var(--Grey-100)',
              color: 'var(--Text-Primary)',
              borderRadius: 'var(--button-radius)',
              fontWeight: 'var(--font-weight-medium)'
            }}
          >
            Cancel
          </button>
          <button 
            onClick={onConfirm}
            className="px-6 py-2"
            style={{
              backgroundColor: 'var(--Red-600)',
              color: 'var(--Text-Contrast)',
              borderRadius: 'var(--button-radius)',
              fontWeight: 'var(--font-weight-medium)'
            }}
          >
            Delete
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const EthicalModal = ({ isOpen, value, onChange, error, onSave, onCancel }) => {
  if (!isOpen) return null;
  
  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 'var(--z-modal, 9999)' }}>
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
      <div className="relative bg-white shadow-2xl max-w-2xl w-full p-6 animate-fadeIn" style={{
        borderRadius: 'var(--radius-xl)',
        backgroundColor: 'var(--Surface-Primary)'
      }}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 flex items-center justify-center" style={{
            background: 'var(--brand-gradient-1)',
            borderRadius: 'var(--radius-s)'
          }}>
            <SparklesIcon className="w-5 h-5" style={{ color: 'var(--Text-Contrast)' }} />
          </div>
          <h3 style={{
            fontSize: 'var(--font-size-subtitle1)',
            fontWeight: 'var(--font-weight-subtitle2)',
            color: 'var(--Text-Primary)'
          }}>Your Ethical Jeweller Statement</h3>
        </div>
        
        <p style={{ color: 'var(--Text-Secondary)', marginBottom: 'var(--spacing-s)' }}>
          To select "Ethical Jeweller", please provide a statement (min. 100 characters) about your practices. 
          This will be shown to customers.
        </p>
        
        <textarea 
          placeholder="What makes you an ethical jeweller?"
          value={value} 
          onChange={(e) => onChange(e.target.value)} 
          rows={6}
          style={{
            width: '100%',
            padding: 'var(--spacing-s)',
            borderRadius: 'var(--radius-s)',
            border: error ? '1px solid var(--Red-600)' : '1px solid var(--Stroke-Secondary)',
            fontSize: 'var(--font-size-body3)',
            resize: 'vertical'
          }}
        />
        
        <div className="flex justify-between items-center mt-2 mb-4">
          {error ? (
            <span style={{ fontSize: 'var(--font-size-body4)', color: 'var(--Text-Error)' }}>
              {error}
            </span>
          ) : (
            <span style={{ fontSize: 'var(--font-size-body4)', color: 'var(--Text-Secondary)' }}>
              Minimum 100 characters
            </span>
          )}
          <span style={{ 
            fontSize: 'var(--font-size-body4)',
            color: value.length >= 100 ? 'var(--Green-600)' : 'var(--Text-Secondary)'
          }}>
            {value.length} characters
          </span>
        </div>
        
        <div className="flex gap-3 justify-end">
          <button 
            onClick={onCancel}
            className="px-6 py-2"
            style={{
              backgroundColor: 'var(--Grey-100)',
              color: 'var(--Text-Primary)',
              borderRadius: 'var(--button-radius)',
              fontWeight: 'var(--font-weight-medium)'
            }}
          >
            Cancel
          </button>
          <button 
            onClick={onSave}
            className="px-6 py-2"
            style={{
              background: 'var(--brand-gradient-1)',
              color: 'var(--Text-Contrast)',
              borderRadius: 'var(--button-radius)',
              fontWeight: 'var(--font-weight-medium)'
            }}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export const PortfolioSection = () => {
  const { formData, updateField } = useProfileFormContext();
  const [awards, setAwards] = useState(formData.awards || []);
  const [isEthicalModalOpen, setIsEthicalModalOpen] = useState(false);
  const [ethicalStatementInput, setEthicalStatementInput] = useState(formData.ethicalStatement || '');
  const [ethicalModalError, setEthicalModalError] = useState('');
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [awardToDeleteIndex, setAwardToDeleteIndex] = useState(null);

  const handleAddAward = () => {
    if (awards.length < 6) {
      const newAwards = [...awards, { category: 'Award Won', title: '', about: '' }];
      setAwards(newAwards);
      updateField('awards', newAwards);
    }
  };

  const handleAwardChange = (index, field, value) => {
    const newAwards = awards.map((award, i) =>
      i === index ? { ...award, [field]: value } : award
    );
    setAwards(newAwards);
    updateField('awards', newAwards);
  };
  
  const promptRemoveAward = (index) => {
    setAwardToDeleteIndex(index);
    setDeleteModalOpen(true);
  };

  const confirmRemoveAward = () => {
    if (awardToDeleteIndex !== null) {
      const newAwards = awards.filter((_, i) => i !== awardToDeleteIndex);
      setAwards(newAwards);
      updateField('awards', newAwards);
    }
    setDeleteModalOpen(false);
    setAwardToDeleteIndex(null);
  };
  
  const handleTagChange = (tag, value) => {
    if (tag === 'ethicalJeweller') {
      if (value === true) {
        setEthicalStatementInput(formData.ethicalStatement || '');
        setEthicalModalError('');
        setIsEthicalModalOpen(true);
      } else {
        updateField('ethicalStatement', '');
      }
    } else {
      updateField('tags', { ...formData.tags, [tag]: value });
    }
  };

  const handleSaveEthicalStatement = () => {
    if (ethicalStatementInput.trim().length < 100) {
      setEthicalModalError('Statement must be at least 100 characters long.');
      return;
    }
    
    updateField('ethicalStatement', ethicalStatementInput);
    setIsEthicalModalOpen(false);
    setEthicalModalError('');
  };

  const handleCancelEthicalModal = () => {
    setIsEthicalModalOpen(false);
    setEthicalModalError('');
  };

  const businessTags = [
    { key: 'ethicalJeweller', label: 'Ethical Jeweller' },
    { key: 'emergingDesigner', label: 'Emerging Designer' },
    { key: 'freeRingResizing', label: 'Free Ring Resizing' },
    { key: 'offersWarranty', label: 'Offers Warranty' },
    { key: 'lgbtqOwned', label: 'LGBTQ+ Owned' },
    { key: 'queerFriendly', label: 'Queer Friendly' },
    { key: 'madeUK', label: 'Made in UK' },
    { key: 'inPersonConsultations', label: 'In-person Consultations' },
  ];

  const hasValidEthicalStatement = formData.ethicalStatement && 
                                   formData.ethicalStatement.trim().length >= 100;

  return (
    <>
      <div className="card" style={{
        backgroundColor: 'var(--Surface-Primary)',
        borderRadius: 'var(--radius-xl)',
        padding: 'var(--spacing-l)',
        boxShadow: 'var(--shadow-frame-small)'
      }}>
        {/* Section Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 flex items-center justify-center" style={{
              background: 'var(--brand-gradient-2)',
              borderRadius: 'var(--radius-s)'
            }}>
              <BriefcaseIcon className="w-5 h-5" style={{ color: 'var(--Gray-900)' }} />
            </div>
            <h2 style={{
              fontSize: 'var(--font-size-h5)',
              fontWeight: 'var(--font-weight-h5)',
              color: 'var(--Text-Primary)',
              lineHeight: 'var(--line-height-h5)'
            }}>
              Portfolio & Recognition
            </h2>
          </div>
          <p style={{ 
            color: 'var(--Text-Secondary)',
            fontSize: 'var(--font-size-body3)',
            marginLeft: '52px'
          }}>
            This section is all about showcasing your strengths. Upload a video, add a gallery of your work, share achievements, and highlight values. These are the details that help your brand stand out and connect with the right audience.
          </p>
        </div>

        <div style={{
          height: '1px',
          background: 'var(--Grey-300)',
          margin: 'var(--spacing-xl) 0',
          width: '100%' 
        }} />

        {/* Video Showcase */}
        <div className="mb-8">
          <FormField
            label="Video Introduction"
            hideCompletionIcon={true}
            labelStyle={{
              fontSize: 'var(--font-size-subtitle1)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--Text-Primary)'
            }}
            labelAction={
              <a
                href="https://firebasestorage.googleapis.com/v0/b/boutee-app.firebasestorage.app/o/Introduction%20Video%20Guide.pdf?alt=media&token=c1663a57-78ef-4038-9c89-9358228e864e"
                target="_blank"
                rel="noreferrer"
                style={{
                  padding: '6px 12px',
                  borderRadius: '999px',
                  border: '1px solid var(--Purple-200)',
                  background: 'var(--Purple-50)',
                  color: 'var(--Purple-800)',
                  fontSize: 'var(--font-size-body5)',
                  fontWeight: 'var(--font-weight-medium)',
                  textDecoration: 'none',
                  whiteSpace: 'nowrap',
                  boxShadow: 'var(--shadow-sm)'
                }}
              >
                What is this and why do I need to do it?
              </a>
            }
          >
          <p style={{ 
            color: 'var(--Text-Secondary)',
            fontSize: 'var(--font-size-body3)',
            margin: 'var(--spacing-s) 0',
          }}>
            Jewellers with video introductions get 3x more favourites. Customers want to see the person behind the brand - it builds trust and connection. Click the button above to find out why it's important and for some tips for making it great.
          </p>
            <VideoUpload
              value={formData.showcaseVideo}
              onChange={(url) => updateField('showcaseVideo', url)}
            />
          </FormField>
        </div>
        
        {/* Gallery */}
        <div className="mb-8">
          <FormField 
            label="Gallery" 
            hideCompletionIcon={true}
            labelStyle={{
              fontSize: 'var(--font-size-subtitle1)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--Text-Primary)'
            }}
          >
          <p style={{ 
            color: 'var(--Text-Secondary)',
            fontSize: 'var(--font-size-body3)',
            margin: 'var(--spacing-s) 0',
          }}>
            Showcase your best work - upload high-quality images that represent your brand and style. These can be images of your pieces, your design process, bench shots or even happy customers (with their permission!). Make sure they're high quality and varied.
          </p>
            <ImageUpload 
              value={formData.galleryImages || []} 
              onChange={(urls) => updateField('galleryImages', urls)} 
              maxFiles={12}
            />
            <div className="mt-3 flex items-center gap-3">
              <span style={{ fontSize: 'var(--font-size-body4)', color: 'var(--Text-Secondary)' }}>
                {formData.galleryImages?.length || 0} of 12 photos uploaded
              </span>
              <div className="flex-1 h-2 overflow-hidden max-w-xs" style={{
                backgroundColor: 'var(--Grey-200)',
                borderRadius: 'var(--radius-pill)'
              }}>
                <div 
                  className="h-full transition-all duration-300"
                  style={{ 
                    background: 'var(--brand-gradient-1)',
                    width: `${((formData.galleryImages?.length || 0) / 12) * 100}%`,
                    borderRadius: 'var(--radius-pill)'
                  }}
                />
              </div>
            </div>
          </FormField>
        </div>

        <div style={{
          height: '1px',
          background: 'var(--Grey-300)',
          margin: 'var(--spacing-xl) 0',
          width: '100%'
        }} />
        
        {/* Awards & Recognition */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <h3 style={{
              fontSize: 'var(--font-size-subtitle1)',
              fontWeight: 'var(--font-weight-subtitle1)',
              color: 'var(--Text-Primary)'
            }}>
              Awards & Recognition
            </h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {awards.map((award, index) => (
              <div key={index} className="p-4 space-y-3" style={{
                borderRadius: 'var(--radius-m)',
                background: 'var(--brand-gradient-2)'
              }}>
                <div className="flex justify-between items-start">
                  <span style={{
                    fontSize: 'var(--font-size-subtitle3)',
                    fontWeight: 'var(--font-weight-semibold)',
                    textTransform: 'none',
                    color: 'var(--Grey-900)'
                  }}>
                    Achievement #{index + 1}
                  </span>
                  <button
                    onClick={() => promptRemoveAward(index)}
                    className="p-1 rounded-lg hover:bg-red-100 transition-colors group"
                    title="Remove this achievement"
                  >
                    <TrashIcon className="w-4 h-4 text-gray-800 group-hover:text-red-600" />
                  </button>
                </div>
                
                <select 
                  value={award.category} 
                  onChange={(e) => handleAwardChange(index, 'category', e.target.value)}
                  className="form-select"
                  style={{
                    width: '100%',
                    padding: 'var(--spacing-s)',
                    paddingRight: '2.5rem',
                    borderRadius: 'var(--radius-s)',
                    border: '1px solid var(--Stroke-Secondary)',
                    fontSize: 'var(--font-size-body3)',
                    backgroundColor: 'var(--Surface-Primary)',
                  }}
                >
                  <option value="Professional Membership">Professional Membership</option>
                  <option value="Award Won">Award Won</option>
                  <option value="Award Nomination">Award Nomination</option>
                  <option value="Qualification">Qualification</option>
                </select>
                
                <input 
                  type="text"
                  placeholder="Title / Organisation"
                  value={award.title} 
                  onChange={(e) => handleAwardChange(index, 'title', e.target.value)}
                  style={{
                    width: '100%',
                    padding: 'var(--spacing-s)',
                    borderRadius: 'var(--radius-s)',
                    border: '1px solid var(--Stroke-Secondary)',
                    fontSize: 'var(--font-size-body3)',
                    backgroundColor: 'var(--Surface-Primary)'
                  }}
                />
                
                <textarea 
                  placeholder="About / Description"
                  value={award.about} 
                  onChange={(e) => handleAwardChange(index, 'about', e.target.value)}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: 'var(--spacing-s)',
                    borderRadius: 'var(--radius-s)',
                    border: '1px solid var(--Stroke-Secondary)',
                    fontSize: 'var(--font-size-body3)',
                    resize: 'vertical',
                    backgroundColor: 'var(--Surface-Primary)'
                  }}
                />
              </div>
            ))}
          </div>
          
          <button
            onClick={handleAddAward}
            disabled={awards.length >= 6}
            className="flex items-center gap-2 px-6 py-2"
            style={{
              backgroundColor: awards.length >= 6 ? 'var(--Grey-100)' : 'transparent',
              color: awards.length >= 6 ? 'var(--Text-Disabled)' : 'var(--Text-Primary)',
              border: awards.length >= 6 ? 'none' : '1px solid var(--Stroke-Primary)',
              borderRadius: 'var(--button-radius)',
              fontWeight: 'var(--font-weight-medium)',
              opacity: awards.length >= 6 ? 0.5 : 1,
              cursor: awards.length >= 6 ? 'not-allowed' : 'pointer'
            }}
            title={awards.length >= 6 ? "You can add a maximum of 6 achievements." : "Add a new achievement"}
          >
            <PlusIcon className="w-4 h-4" />
            Add Achievement
          </button>
        </div>

        <div style={{
          height: '1px',
          background: 'var(--Grey-300)',
          margin: 'var(--spacing-xl) 0',
          width: '100%'
        }} />
        
        {/* Business Tags */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <h3 style={{
              fontSize: 'var(--font-size-subtitle1)',
              fontWeight: 'var(--font-weight-subtitle2)',
              color: 'var(--Text-Primary)'
            }}>
              Business Tags
            </h3>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {businessTags.map(({ key, label }) => {
              const isChecked = key === 'ethicalJeweller' 
                ? hasValidEthicalStatement
                : !!formData.tags?.[key];
              
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleTagChange(key, !isChecked)}
                  className="flex items-center justify-between p-3 transition-colors cursor-pointer group text-left"
                  style={{
                    borderRadius: 'var(--radius-s)',
                    backgroundColor: 'var(--Grey-50)',
                    ':hover': { backgroundColor: 'var(--Grey-100)' }
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--Grey-100)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--Grey-50)'}
                  aria-pressed={isChecked}
                >
                  <span style={{
                    fontSize: 'var(--font-size-body4)',
                    fontWeight: 'var(--font-weight-medium)',
                    color: 'var(--Text-Primary)'
                  }}>
                    {label}
                  </span>
                  <div className="relative">
                    <div className="w-11 h-6 rounded-full transition-colors duration-200" style={{
                      background: isChecked ? 'var(--brand-gradient-1)' : 'var(--Grey-300)'
                    }}>
                      <div className="absolute top-0.5 w-5 h-5 rounded-full shadow-md transform transition-transform duration-200"
                           style={{
                             backgroundColor: 'var(--Surface-Primary)',
                             transform: isChecked ? 'translateX(1.25rem)' : 'translateX(0.125rem)'
                           }} />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        
        {/* Ethical Statement */}
        {hasValidEthicalStatement && (
          <div className="p-6" style={{
            background: 'var(--Green-100)',
            borderRadius: 'var(--radius-m)'
          }}>
            <FormField 
              label="Ethical Jeweller Statement" 
              required
              hideCompletionIcon={true}
              labelStyle={{
                fontSize: 'var(--font-size-subtitle3)',
                fontWeight: 'var(--font-weight-bold)',
                color: 'var(--Text-Primary)'
              }}
            >
              <textarea 
                value={formData.ethicalStatement || ''} 
                onChange={(e) => updateField('ethicalStatement', e.target.value)} 
                rows={4}
                style={{
                  width: '100%',
                  padding: 'var(--spacing-s)',
                  borderRadius: 'var(--radius-s)',
                  border: '1px solid var(--Stroke-Secondary)',
                  fontSize: 'var(--font-size-body3)',
                  resize: 'vertical',
                  backgroundColor: 'var(--Surface-Primary)'
                }}
              />
              <div className="mt-2 flex justify-between items-center">
                <span style={{ fontSize: 'var(--font-size-body5)', color: 'var(--Grey-800)' }}>
                  Minimum 100 characters
                </span>
                <span style={{
                  fontSize: 'var(--font-size-body5)',
                  fontWeight: 'var(--font-weight-medium)',
                  color: formData.ethicalStatement?.length >= 100 ? 'var(--Grey-800)' : 'var(--Amber-800)'
                }}>
                  Currently: {formData.ethicalStatement?.length || 0}
                </span>
              </div>
            </FormField>
          </div>
        )}

      {/* Combined Tips Section */}
      <div className="mt-8" style={{
        background: 'var(--brand-gradient-2)',
        borderRadius: 'var(--radius-m)',
        padding: 'var(--spacing-m)',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--spacing-s)'
      }}>
        <span style={{ fontSize: '25px', marginTop: '0px' }}>💡</span>
        <div>
          <h6 style={{
            fontSize: 'var(--font-size-h6)',
            fontWeight: 'var(--font-weight-semibold)',
            color: 'var(--Text-Primary)',
            marginBottom: 'var(--spacing-xxs)',
            margin: 0
          }}>
            Tips for this section
          </h6>
          <p style={{
            fontSize: 'var(--font-size-body4)',
            color: 'var(--Grey-900)',
            margin: 0,
            marginTop: 'var(--spacing-xxs)',
            lineHeight: '2'
          }}>
          </p>
          <p style={{ fontSize: 'var(--font-size-body4)', color: 'var(--Grey-900)', lineHeight: '2'  }}>
            • You must add a video introduction - customers love seeing the person behind the brand.
          </p>
          <p style={{ fontSize: 'var(--font-size-body4)', color: 'var(--Grey-900)', lineHeight: '2'  }}>
            • Showcase your best work - upload high-quality images.
          </p>
          <p style={{ fontSize: 'var(--font-size-body4)', color: 'var(--Grey-900)', lineHeight: '2'  }}>
            • Highlight your achievements - awards and recognitions build trust.
          </p>
          <p style={{ fontSize: 'var(--font-size-body4)', color: 'var(--Grey-900)', lineHeight: '2'  }}>
            • Make sure your Business Tags accurately reflect your values and services.
          </p>
        </div>
      </div>
      </div>

      {/* Modals rendered with portals */}
      <DeleteModal 
        isOpen={isDeleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmRemoveAward}
      />
      
      <EthicalModal 
        isOpen={isEthicalModalOpen}
        value={ethicalStatementInput}
        onChange={setEthicalStatementInput}
        error={ethicalModalError}
        onSave={handleSaveEthicalStatement}
        onCancel={handleCancelEthicalModal}
      />
    </>

  );
};
